import { notification } from 'antd';

import { intl } from '../../src-new/providers/GlobalInternalizationProvider';

import {
  deleteChatReply,
  deleteSingleMessage,
  downloadChatUrl,
  getFileUsingSignedUrl,
  getProjectById,
  getUser,
  getUsers,
  getWorkspaceById,
  getWorkspaces,
  incrementChatUnreadCount,
  updateProjectSections,
} from '../../actions';
import {
  addChatMessage,
  addChatReply,
  editMessageDetails,
  setScrollPosition,
  updateSingleMessage,
} from '../../actions/messageAction';
import { fetchReceivedProjectById } from '../../actions/projectActions';
import { getSocketConfig, socketClient } from '../../socket/socketClient';
import {
  addToUserFilterResultsAction,
  deleteUserFilterResultsAction,
} from '../general/actions';
import { getSectionCountsAction } from '../sections/actions';
import {
  newSocketNotification,
  syncNotificationsAfterReconnect,
} from '../state-areas/notifications/notificationActions';
import { ACTION_TYPES as SOCKET_ACTION_TYPES } from '../state-areas/socket/constants';
import { ACTION_TYPES as USER_ACTION_TYPES } from '../state-areas/user/constants';
import { ACTION_TYPES as PROJECTS_ACTION_TYPES } from '../projects/constants';
import {
  addCommentToTaskAction,
  addSubTaskEventAction,
  addTaskEventAction,
  deleteCommentFromTaskAction,
  getTaskAction,
  removeTaskEventAction,
  reorderTasksEventAction,
  updateTaskEventAction,
} from '../tasks/actions';
import {
  setSocketConnectedAction,
  setSocketDisconnectedAction,
} from '../state-areas/socket/actions';
import { remindersApi } from '../../services/remindersApi';

const EVENT_TYPES = {
  SECTIONS_ARRANGED: 'sections-arranged',
  SECTIONS_REORDERED: 'sections-reordered',
  TASK_ADDED: 'TaskAdded',
  TASK_UPDATED: 'UpdateTaskEvent',
  TASK_REMOVED: 'TaskRemoved',
  SUBTASK_ADDED: 'SubtaskAdded',
  MESSAGE_RECEIVED: 'message-received',
  CHAT_RECEIVED: 'chat-received',
  MESSAGE_EDITED: 'message-edited',
  MESSAGE_DELETED: 'message-deleted',
  MESSAGE_REPLY: 'message-reply',
  REPLY_EDITED: 'reply-edited',
  REPLY_DELETED: 'reply-deleted',
  PROJECT_ADDED: 'ProjectAdded',
  PROJECT_UPDATED: 'ProjectUpdated',
  PROJECT_DELETED: 'ProjectDeleted',
  ADDED_TO_PROJECT: 'AddedToProject',
  INVITE_ACCEPTED: 'InviteAccepted',
  COLLABORATORS_ADDED: 'collaborators-added',
  USER_ADDED: 'user-added-successfully',
  USER_UPDATED: 'user-updated',
  NOTIFICATION_UPDATED: 'UpdateNotification',
  NOTIFICATION_NEW: 'notification-new',
  REMINDER_FIRED: 'reminder-fired',
  REMINDERS_INVALIDATED: 'reminders-invalidated',
};

const MAX_RETRIES = 20;
const RETRY_DELAY = 2000;

const socketMiddleware = (store) => {
  const fn = '[socketMiddleware]';
  const { dispatch, getState } = store;

  let socketConnection = null;

  let retryCount = 0;
  let connectionErrorCount = 0;
  let retryTimer = null;
  let isSocketConnected = false;
  let socketRecoveryNeeded = true;

  const setSocketConnected = () => {
    isSocketConnected = true;
    dispatch(setSocketConnectedAction());
  };
  const setSocketDisconnected = () => {
    isSocketConnected = false;
    dispatch(setSocketDisconnectedAction());
  };

  const cleanupSocket = () => {
    if (socketConnection) {
      console.log(
        `${fn}: cleanupSocket. Will remove listeners and then close current connection`,
      );
      socketConnection.removeAllListeners();
      socketConnection.disconnect();
      socketConnection = null;
    }
    if (retryTimer) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
  };

  const tryClientReconnectionAfterTimeout = () =>
    setTimeout(() => {
      connectionErrorCount = 0;
      tryConnection();
    }, getSocketConfig().reconnectionDelayMax * 10);

  const tryConnection = () => {
    // Clean up any existing socket
    cleanupSocket();
    console.log(
      `${fn}: Going to create new socket connection ${socketRecoveryNeeded ? 'and set listeners' : 'without setting listeners'}`,
    );
    // Create new socket
    socketConnection = socketClient.connect();

    socketConnection.on('connect', () => {
      connectionErrorCount = 0;
      console.log(
        `${fn}: Socket connected (attempt ${retryCount + 1}), awaiting auth...`,
      );
      setSocketConnected();
      if (socketRecoveryNeeded) {
        setSocketListeners();
        socketRecoveryNeeded = false;
      }
      dispatch(syncNotificationsAfterReconnect());
    });

    socketConnection.on('connect_error', (err) => {
      connectionErrorCount++;
      console.warn(
        `${fn}: Socket connection error:`,
        err.message,
        connectionErrorCount,
      );
      console.log(
        `${fn}: Socket connection error description:`,
        err.description,
      );
      if (connectionErrorCount === getSocketConfig().reconnectionAttempts) {
        socketRecoveryNeeded = true;
        tryClientReconnectionAfterTimeout();
      }
    });

    socketConnection.on('ConnectionRejected', (data) => {
      console.log(
        `${fn}: Connection rejected on attempt ${retryCount + 1}:`,
        data.message,
      );

      if (retryCount < MAX_RETRIES) {
        retryCount++;
        console.log(
          `${fn}: Will retry in ${RETRY_DELAY}ms (attempt ${retryCount} of ${MAX_RETRIES})`,
        );
        if (localStorage.getItem('token')) {
          socketRecoveryNeeded = true;
          retryTimer = setTimeout(tryConnection, RETRY_DELAY);
        }
      } else {
        console.log(`${fn}: Max retries reached`);
      }
    });

    socketConnection.on('reconnect', () => {
      console.log(`${fn}: Socket reconnected:`);
    });

    socketConnection.on('disconnect', (reason) => {
      console.warn(`${fn}: Socket disconnected:`, reason);
      setSocketDisconnected();
      if (!socketConnection.active) {
        console.warn(
          `${fn}: socket connection is not active anymore. Needs manual reconnection.`,
        );
        socketRecoveryNeeded = true;
        tryConnection();
      }
    });

    socketConnection.on('error', (error) => {
      console.warn(`${fn}: Socket error:`, error);
      retryCount = 0;
      socketRecoveryNeeded = true;
      tryConnection();
    });
  };

  const componentLevelListeners = new Map();

  const logSocketEvent = (eventName, data) => {
    if (localStorage.getItem('debugMode') !== 'true') {
      return;
    }

    console.debug(`${fn}: --- ${eventName} ---:`, data);
  };

  const setSocketListeners = async () => {
    const permission = await Notification.requestPermission();
    const isNotificationsPermissionGranted = permission === 'granted';

    socketConnection.onAny((eventName, data) => {
      logSocketEvent(eventName, data);
      switch (eventName) {
        case EVENT_TYPES.SECTIONS_ARRANGED: {
          const { 1: projectId } = window.location.pathname.split('/');
          const projectIdReceived = data?.projectId;
          const updatedSections = data?.sections;
          if (projectIdReceived?.toString() === projectId?.toString()) {
            dispatch(updateProjectSections(updatedSections, projectIdReceived));
          }
          // dispatch(getProjectById(projectId, 'TaskBoard sections-rearranged'));
          break;
        }
        case EVENT_TYPES.SECTIONS_REORDERED: {
          dispatch(reorderTasksEventAction(data));
          const urlSearchParams = new URLSearchParams(window.location.search);
          const selectedTaskParam = urlSearchParams.get('selectedTask');

          if (selectedTaskParam) {
            const isSingleTaskUpdated = data?.tasks?.find(
              (tsk) =>
                tsk?.titleIdentifier?.toString() ===
                selectedTaskParam?.toString(),
            );

            if (isSingleTaskUpdated) {
              dispatch(getTaskAction(isSingleTaskUpdated?.task?.toString()));
            }
          }
          const projectIdFromURL = window.location.pathname.split('/')[1];
          if (projectIdFromURL && projectIdFromURL !== 'all-tasks') {
            dispatch(getSectionCountsAction(projectIdFromURL));
          }
          break;
        }
        case EVENT_TYPES.TASK_ADDED: {
          const { 1: projectId } = window.location.pathname.split('/');
          if (projectId) {
            const projects =
              data?.projects?.filter(
                (p) => p?.projectId?.toString() === projectId?.toString(),
              ) || [];
            data.projects = projects;
          }

          dispatch(addTaskEventAction(data));
          dispatch(addToUserFilterResultsAction(data));
          break;
        }
        case EVENT_TYPES.TASK_UPDATED: {
          const taskId = data?._id;
          if (taskId !== 'board' && taskId !== 'chat') {
            dispatch(updateTaskEventAction(data));
          }
          dispatch(addToUserFilterResultsAction(data));
          break;
        }
        case EVENT_TYPES.TASK_REMOVED: {
          dispatch(removeTaskEventAction(data));
          dispatch(deleteUserFilterResultsAction(data));
          break;
        }
        case EVENT_TYPES.SUBTASK_ADDED: {
          dispatch(addSubTaskEventAction(data));
          break;
        }
        case EVENT_TYPES.MESSAGE_RECEIVED: {
          dispatch(addCommentToTaskAction(data));
          // TODO: Global handler if needed, for example for recent activity
          break;
        }
        case EVENT_TYPES.CHAT_RECEIVED: {
          const regex = /\/chat\/([a-zA-Z0-9]+)/;
          const url = window?.location?.pathname;
          const match = url && url.match(regex);
          const chatRoomId = match ? match[1] : url.split('/')[1];
          const chats = getState().chatReducer.chats;

          dispatch(incrementChatUnreadCount(data));
          if (data?.roomId?.toString() === chatRoomId?.toString()) {
            dispatch(addChatMessage(chatRoomId, data));
            dispatch(setScrollPosition());
            data &&
              data.files &&
              data.files.map(async (file) => {
                const { signedUrl, fileId } = await dispatch(
                  downloadChatUrl({
                    fileId: file,
                    chatId: data.roomId,
                  }),
                );
                if (signedUrl) {
                  await dispatch(getFileUsingSignedUrl(signedUrl, fileId));
                }
              });
          } else {
            // Chat not currently open — just update unread count (already done above)
            // Only refetch workspace if this is a brand new chat not in state
            const isChatInState = chats?.find((c) => c?._id?.toString() === data?.roomId?.toString());
            if (!isChatInState) {
              dispatch(getWorkspaceById());
            }
          }
          break;
        }
        case EVENT_TYPES.MESSAGE_EDITED: {
          if (data?.context === 'chat') {
            const regex = /\/chat\/([a-zA-Z0-9]+)/;
            const url = window?.location?.pathname;
            const match = url && url.match(regex);
            const chatRoomId = match ? match[1] : url.split('/')[1];

            if (data?.roomId === chatRoomId) {
              dispatch(
                editMessageDetails({
                  roomId: chatRoomId,
                  msgId: data?._id,
                  messageDescription: data?.messageDescription,
                }),
              );
            }
            // Message edit doesn't require full workspace refetch — local state already updated
          } else if (data?.context === 'task') {
            dispatch(addCommentToTaskAction(data));
          }
          break;
        }
        case EVENT_TYPES.MESSAGE_DELETED: {
          // dispatch(getChatRooms(query));
          const context = data?.context;
          const messageId = data?.messageId;
          if (context?.type === 'task') {
            dispatch(deleteCommentFromTaskAction(messageId, context?._id));
          } else if (context?.type === 'project') {
            dispatch(deleteSingleMessage({ roomId: context?._id, messageId }));
            // TODO: Remove messageId from project chat. Match active project _id with context._id
          } else if (context?.type === 'chat') {
            dispatch(deleteSingleMessage({ roomId: context?._id, messageId }));
            // TODO: Remove messageId from chat. Match active chat _id with context._id or check if chat messages are present in state.
          }
          break;
        }
        case EVENT_TYPES.MESSAGE_REPLY: {
          const regex = /\/chat\/([a-zA-Z0-9]+)/;
          const url = window?.location?.pathname;
          const match = url && url.match(regex);
          const chatRoomId = match ? match[1] : url.split('/')[1];
          const msgId = new URLSearchParams(window?.location?.search).get(
            'msgId',
          );

          if (
            data?.roomId?.toString() === chatRoomId?.toString() ||
            msgId?.toString() === data?._id?.toString()
          ) {
            // dispatch(updateSingleMessage(message));
            dispatch(addChatReply(data));
            dispatch(setScrollPosition());
            data &&
              data.files &&
              data.files.map(async (file) => {
                const { signedUrl, fileId } = await dispatch(
                  downloadChatUrl({
                    fileId: file,
                    chatId: data.roomId,
                  }),
                );
                if (signedUrl) {
                  await dispatch(getFileUsingSignedUrl(signedUrl, fileId));
                }
              });
            // dispatch(getMessages(chat.roomId, 'chat-received'));
          }
          // Non-active room: notification-new socket event handles the pending banner
          break;
        }
        case EVENT_TYPES.REPLY_EDITED: {
          const regex = /\/chat\/([a-zA-Z0-9]+)/;
          const url = window?.location?.pathname;
          const match = url && url.match(regex);
          const chatRoomId = match ? match[1] : url.split('/')[1];

          if (data?.roomId?.toString() === chatRoomId?.toString()) {
            // dispatch(
            //   editChatReply({
            //     roomId: chatRoomId,
            //     messageId: data?._id,
            //     messageDescription: data?.repliedMsg,
            //     parent: data?.parent
            //   })
            // );
            dispatch(updateSingleMessage(data));
          }
          break;
        }
        case EVENT_TYPES.REPLY_DELETED: {
          dispatch(deleteChatReply(data));
          break;
        }
        case EVENT_TYPES.PROJECT_ADDED: {
          const workspaceId = localStorage.getItem('workspaceId');
          if (workspaceId?.toString() === data?.workspace?.toString()) {
            dispatch({
              type: PROJECTS_ACTION_TYPES.ADD_PROJECT_REQUEST_SUCCESS, //'add-project',
              payload: data,
            });
          }
          break;
        }
        case EVENT_TYPES.PROJECT_UPDATED: {
          break;
        }
        case EVENT_TYPES.PROJECT_DELETED: {
          dispatch({
            type: 'delete-project',
            payload: data?._id,
          });
          break;
        }
        case EVENT_TYPES.ADDED_TO_PROJECT: {
          const { 1: selectedProjectId } = window.location.pathname.split('/');
          if (selectedProjectId?.toString() === data?.toString()) {
            dispatch(getProjectById(data));
          } else {
            dispatch(fetchReceivedProjectById(data));
          }
          break;
        }
        case EVENT_TYPES.INVITE_ACCEPTED: {
          if (data?.targetType === 'workspace') {
            dispatch(getWorkspaces('lean'));
          }
          break;
        }
        case EVENT_TYPES.COLLABORATORS_ADDED: {
          const { roomTitle, membersArray: members } = data;
          const isMember = members.includes(localStorage.getItem('Id'));
          if (isMember)
            notification.success({
              message: intl.formatMessage(
                {
                  id: 'YOU_WERE_ADDED_TO_WORKSPACE',
                  defaultMessage: `You were added as a participant in ${roomTitle} workspace`,
                },
                { roomTitle },
              ),
              placement: 'bottomRight',
            });
          dispatch(getWorkspaces('lean'));
          break;
        }
        case EVENT_TYPES.USER_ADDED: {
          dispatch(getUsers());
          break;
        }
        case EVENT_TYPES.USER_UPDATED: {
          dispatch(getUser(data));
          break;
        }
        case EVENT_TYPES.NOTIFICATION_NEW: {
          dispatch(newSocketNotification(data));
          break;
        }
        case EVENT_TYPES.NOTIFICATION_UPDATED: {
          break;
        }
        case EVENT_TYPES.REMINDER_FIRED: {
          const tags = ['Reminder'];
          if (data?.taskId) tags.push({ type: 'Reminder', id: data.taskId });
          dispatch(remindersApi.util.invalidateTags(tags));
          break;
        }
        case EVENT_TYPES.REMINDERS_INVALIDATED: {
          dispatch(remindersApi.util.invalidateTags(['Reminder']));
          break;
        }
        default: {
          console.debug(`${fn}: Unhandled event - ${eventName}`);
        }
      }
    });

    if (componentLevelListeners.size === 0) {
      return;
    }

    componentLevelListeners.forEach((componentLevelListenerData) => {
      if (
        componentLevelListenerData?.eventName &&
        componentLevelListenerData?.listener
      ) {
        socketConnection.on(
          componentLevelListenerData?.eventName,
          componentLevelListenerData?.listener,
        );
      }
    });
  };

  const emitSocketMessage = (payload) => {
    socketConnection.emit(payload?.type, payload?.data);
  };

  return (next) => (action) => {
    const result = next(action);

    switch (action.type) {
      case USER_ACTION_TYPES.GET_USER_REQUEST_SUCCESS: {
        if (!socketConnection) {
          socketRecoveryNeeded = true;
          tryConnection();
        }
        break;
      }
      case SOCKET_ACTION_TYPES.SOCKET_SEND: {
        emitSocketMessage(action?.payload);
        break;
      }
      case SOCKET_ACTION_TYPES.REGISTER_COMPONENT_LEVEL_SOCKET_LISTENER: {
        componentLevelListeners.set(action?.payload?.listenerId, {
          listener: action?.payload?.listener,
          eventName: action?.payload?.eventName,
        });
        // if no socketConnection, it means that tryConnection hasn't happened yet. In that case setting the listener right away is not needed.
        // This listener will be set on successful connect event from componentLevelListeners map
        if (socketConnection) {
          socketConnection?.on(
            action?.payload?.eventName,
            action?.payload?.listener,
          );
        }
        break;
      }
      case SOCKET_ACTION_TYPES.UNREGISTER_COMPONENT_LEVEL_SOCKET_LISTENER: {
        const listener = componentLevelListeners.get(
          action?.payload?.listenerId,
        )?.listener;

        if (!listener) {
          return result;
        }

        socketConnection?.off(action?.payload?.eventName, listener);
        componentLevelListeners.delete(action?.payload?.listenerId);
        break;
      }
      default:
        return result;
    }

    return result;
  };
};

export default socketMiddleware;
